import * as React from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';

import Document from '../../../../../shared/shim/react-pdf/PreDocument';
import Page from '../../../../../shared/shim/react-pdf/OriPage';
import FooterWrapper, {
    inferFooterWrapperUploadRework,
} from '../FooterWrapper';
import DocumentFooter, {
    inferUploadReworkDocumentModalFooter,
} from './DocumentModalFooter';

import { useClickAway, useUpdateEffect } from 'react-use';
import isPointerInside from 'shared/helpers/isPointerInside';
import { CircularProgress, Typography } from '@mui/material';
import { DocumentPDF } from 'modules/home/manage-packets/solicitor-viewer/components/DocumentViewer';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';

import {
    DocumentCSV,
    // eslint-disable-next-line import/no-named-default
    default as FortuneSheetViewer,
} from 'shared/components/FortuneSheetViewer';
import FibersViewer, { inferImageRedacted } from 'shared/components/FibersView';
// interface populatedRedacts {
//   [key : number] : Redacts[],
// }

const DocumentModal: React.FC<Props> = React.forwardRef(
    (
        {
            callbackClosingModal,
            callbackOpenModal,
            documentMessage,
            documentFile,
            documentPlaceholder,
            documentReason,
        },
        ref
    ): React.ReactElement => {
        const containerModalRef = React.useRef<HTMLDivElement | null>(null);
        const [numPages, setNumPages] = React.useState<number | null>(null);
        const [pdfUrl, setPdfUrl] = React.useState<string | null>(null);
        const [pdfInitialization, setPdfInitialization] =
            React.useState<boolean>(false);
        const [isDocumentSuccess, setLoadingDocumentSuccess] =
            React.useState<boolean>(false);

        const documentRef = React.useRef<DocumentPDF | null>(null);
        const footerWrapperRef =
            React.useRef<inferFooterWrapperUploadRework | null>(null);
        const footerRef =
            React.useRef<inferUploadReworkDocumentModalFooter | null>(null);
        const handle = useFullScreenHandle();
        const [isFullScreen, setFullScreen] = React.useState<boolean>(false);
        // const [historyRedacts, setRedactsHistory] = React.useState<populatedRedacts>({});
        const isCSVUri = React.useMemo(
            () =>
                !!(
                    pdfUrl &&
                    typeof pdfUrl === 'string' &&
                    (pdfUrl.includes('.csv') || pdfUrl.includes('.xlsx'))
                ),
            [pdfUrl]
        );
        const isImageUri = React.useMemo(
            () =>
                !!(
                    pdfUrl &&
                    typeof pdfUrl === 'string' &&
                    (pdfUrl.includes('.png') ||
                        pdfUrl.includes('.jpeg') ||
                        pdfUrl.includes('.jpg'))
                ),
            [pdfUrl]
        );
        const imageURL = React.useMemo(
            () =>
                pdfUrl &&
                typeof pdfUrl === 'string' &&
                (pdfUrl.includes('.png') ||
                    pdfUrl.includes('.jpeg') ||
                    pdfUrl.includes('.jpg'))
                    ? pdfUrl
                    : '',
            [pdfUrl]
        );

        const excelURL = React.useMemo(
            () =>
                pdfUrl &&
                typeof pdfUrl === 'string' &&
                (pdfUrl.includes('.csv') || pdfUrl.includes('.xlsx'))
                    ? pdfUrl
                    : '',
            [pdfUrl]
        );
        useUpdateEffect(() => {
            if (isFullScreen) {
                handle.enter();
            } else {
                handle.exit();
            }
        }, [isFullScreen]);
        // const [historyRedacts, setRedactsHistory] = React.useState<populatedRedacts>({});
        const [isPdfNetworkError, setPdfNetworkError] =
            React.useState<boolean>(false);
        /**
         * handle action when document load is successful
         */
        const onDocumentLoadSuccess = (props: { numPages: any }): void => {
            setNumPages(props.numPages);
            setLoadingDocumentSuccess(true);
            setPdfNetworkError(false);
        };

        // const [historyRedacts, setRedactsHistory] = React.useState<populatedRedacts>({});

        /**
         * handle action when document load is successful
         */
        const onImageLoadSuccess = (): void => {
            setLoadingDocumentSuccess(true);
            setPdfNetworkError(false);
        };
        const [open, setOpen] = React.useState(false);
        React.useLayoutEffect(() => {
            if (!open && isDocumentSuccess) {
                setLoadingDocumentSuccess(false);
                setPdfUrl(null);
                setPdfInitialization(false);
                setPdfNetworkError(false);
            }
        }, [open]);
        React.useEffect(() => {
            if (open && documentFile) {
                setPdfInitialization(true);
                setPdfUrl(documentFile);
                // ManagePacketService.getSasToken({ fileName: documentFile })
                //     .then((response) => {
                //         setPdfUrl(response.data.data.url);
                //     })
                //     .catch((error) => Utils.handleErrorResponse(error));
            }
        }, [documentFile, open]);

        React.useState<boolean>(false);

        const outsideContainerModalRef = React.useRef<HTMLDivElement>(null);

        /**
         * handle clickaway listener for closing modal in backdrop selection
         * @param {MouseEvent} parameteeventrNameHere - event
         */
        const handleClickAwayListener = React.useCallback(
            (event: MouseEvent) => {
                const { current: footerWrapper } = footerWrapperRef;
                const { current: footer } = footerRef;
                const { current: container } = containerModalRef;
                if (
                    container &&
                    footerWrapper?.isPointerOnFooter(event) === false &&
                    isPointerInside(container, event) &&
                    footer?.isModalFooterActive() === false
                ) {
                    setOpen(false);
                }
            },
            []
        );
        useClickAway(outsideContainerModalRef, handleClickAwayListener);

        /**
         * handle modal open
         */
        const handleOpen = React.useCallback((): void => {
            setOpen(true);
            if (typeof callbackOpenModal === 'function') callbackOpenModal();
        }, [callbackOpenModal]);
        /**
         * handle modal close
         */
        const handleClose = React.useCallback((): void => {
            setOpen(false);
            if (typeof callbackClosingModal === 'function')
                callbackClosingModal(null);
        }, [callbackClosingModal]);

        /**
         * handle save process from footer
         */
        const handleSave = React.useCallback(async () => {
            setOpen(false);
            if (typeof callbackClosingModal === 'function')
                callbackClosingModal(null);
        }, [callbackClosingModal]);
        const imageContentRef = React.useRef<inferImageRedacted | null>(null);
        const [width, setWidth] = React.useState<number>(400 + 25 * 10);
        const [height, setHeight] = React.useState<number>(400 + 25 * 10);
        const [zoomPx, setZoomPx] = React.useState<number>(10);

        const handleZoom = React.useCallback(
            (value: number) => {
                if (isCSVUri) {
                    const { current: document } = documentRef;
                    if (document) {
                        const sheet: DocumentCSV = document;
                        if (sheet && typeof sheet.setSheetZoom === 'function') {
                            sheet.setSheetZoom(value / 100);
                        }
                    }
                } else if (isImageUri) {
                    const img: any = imageContentRef.current;

                    const { width, height: imgHeight } =
                        img.getRenderViewport();
                    img.setWidth(width * ((value + 30) / 100));
                    img.setHeight(imgHeight * ((value + 30) / 100));
                }
                setWidth(400 + 25 * value);
                setHeight(400 + 25 * value);
                setZoomPx(value);
            },
            [isCSVUri, isImageUri]
        );
        const handleImageRefresh = React.useCallback(() => {
            const img: any = imageContentRef.current;

            const { width, height: imgHeight } = img.getRenderViewport();
            img.setWidth(width * ((zoomPx + 30) / 100));
            img.setHeight(imgHeight * ((zoomPx + 30) / 100));
        }, [zoomPx]);
        React.useImperativeHandle(
            ref,
            () => ({
                setClose: () => {
                    handleClose();
                },
                setOpen: () => {
                    handleOpen();
                },
            }),
            [handleOpen, handleClose]
        );
        return (
            <Box className="DocumentModal">
                <Modal
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="parent-modal-title"
                    aria-describedby="parent-modal-description"
                    disableScrollLock
                >
                    <Box className="container-rework-document-footer-content">
                        <FullScreen
                            handle={handle}
                            className="handle-fullscreen-viewer"
                            onChange={(state) => {
                                if (!state) setFullScreen(false);
                            }}
                        >
                            <FooterWrapper
                                innerRef={footerRef}
                                documentRef={documentRef}
                                imageContentRef={imageContentRef}
                                ref={footerWrapperRef}
                                Footer={DocumentFooter}
                                documentMessage={documentMessage}
                                documentReason={documentReason}
                                documentPlaceholder={documentPlaceholder}
                                isDocumentSuccess={isDocumentSuccess}
                                onZoomChanged={handleZoom}
                                onClose={handleSave}
                                isFullScreen={isFullScreen}
                                setFullScreen={setFullScreen}
                            >
                                <Box
                                    className="container-document-modal"
                                    ref={containerModalRef}
                                >
                                    <Box
                                        className="inner-container-document"
                                        ref={outsideContainerModalRef}
                                    >
                                        {isCSVUri && (
                                            <FortuneSheetViewer
                                                className="document-viewer"
                                                ref={documentRef}
                                                file={excelURL}
                                                error={
                                                    <Box
                                                        className={
                                                            isPdfNetworkError
                                                                ? 'align-loader network-error'
                                                                : 'align-loader'
                                                        }
                                                    >
                                                        <Typography variant="h3">
                                                            {isPdfNetworkError
                                                                ? 'No internet connection, Please try again!'
                                                                : 'Document Missing'}
                                                        </Typography>
                                                    </Box>
                                                }
                                                noData={
                                                    <Box
                                                        className={
                                                            isPdfNetworkError
                                                                ? 'align-loader network-error'
                                                                : 'align-loader'
                                                        }
                                                    >
                                                        {isDocumentSuccess ===
                                                            false &&
                                                        (pdfUrl ||
                                                            pdfInitialization ===
                                                                false) ? (
                                                            <CircularProgress
                                                                className="mr-2 margin-loader"
                                                                size={54}
                                                                color="inherit"
                                                                title="progress loading"
                                                            />
                                                        ) : (
                                                            <Typography variant="h3">
                                                                {isPdfNetworkError
                                                                    ? 'No internet connection, Please try again!'
                                                                    : 'Document Missing'}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                                loading={
                                                    <Box className="align-loader">
                                                        <CircularProgress
                                                            className="mr-2 margin-loader"
                                                            size={54}
                                                            color="inherit"
                                                            title="progress loading"
                                                        />
                                                    </Box>
                                                }
                                                onLoadSuccess={
                                                    onDocumentLoadSuccess
                                                }
                                                onLoadNetworkError={() => {
                                                    setPdfNetworkError(true);
                                                }}
                                                onRefreshDocument={() => {}}
                                                onScrolling={() => {}}
                                                onLoadStart={() => {}}
                                                onLoadCancelation={() => {}}
                                                onSourceSuccess={() => {}}
                                                onSourceError={() => {}}
                                                onRenderSuccess={() => {}}
                                                onChangeMeasure={() => {}}
                                                onSheetChanged={() => {}}
                                                csvInfo={null}
                                                redactRef={null}
                                                canDrawn={false}
                                                width={0}
                                                height={0}
                                                viewerId={undefined}
                                                inputRef={undefined}
                                                placeholder=""
                                            />
                                        )}
                                        {isImageUri && (
                                            <FibersViewer
                                                isScrolling
                                                canDrawn={false}
                                                _className="viewer"
                                                src={imageURL}
                                                onLoadSuccess={
                                                    onImageLoadSuccess
                                                }
                                                ref={imageContentRef}
                                                onImageRefresh={
                                                    handleImageRefresh
                                                }
                                            />
                                        )}
                                        {isCSVUri === false &&
                                            isImageUri === false && (
                                                <Document
                                                    className="document-viewer"
                                                    ref={documentRef}
                                                    renderMode="canvas"
                                                    file={pdfUrl}
                                                    error={
                                                        <Box className="align-loader">
                                                            <Typography variant="h3">
                                                                {isPdfNetworkError
                                                                    ? 'No internet connection, Please try again!'
                                                                    : 'Document Missing'}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    noData={
                                                        <Box className="align-loader">
                                                            {isDocumentSuccess ===
                                                                false &&
                                                            (pdfUrl ||
                                                                pdfInitialization ===
                                                                    false) ? (
                                                                <CircularProgress
                                                                    className="mr-2 margin-loader"
                                                                    size={54}
                                                                    color="inherit"
                                                                    title="progress loading"
                                                                />
                                                            ) : (
                                                                <Typography variant="h3">
                                                                    {isPdfNetworkError
                                                                        ? 'No internet connection, Please try again!'
                                                                        : 'Document Missing'}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                    loading={
                                                        <Box className="align-loader">
                                                            <CircularProgress
                                                                className="mr-2 margin-loader"
                                                                size={54}
                                                                color="inherit"
                                                                title="progress loading"
                                                            />
                                                        </Box>
                                                    }
                                                    onLoadNetworkError={() => {
                                                        setPdfNetworkError(
                                                            true
                                                        );
                                                    }}
                                                    onLoadSuccess={
                                                        onDocumentLoadSuccess
                                                    }
                                                >
                                                    {Array.from({
                                                        length: numPages ?? 0,
                                                    }).map((el, index) => (
                                                        <Page
                                                            className="page-document-viewer"
                                                            key={index}
                                                            pageNumber={
                                                                index + 1
                                                            }
                                                            loading={
                                                                <Box className="align-loader">
                                                                    <CircularProgress
                                                                        className="mr-2 margin-loader"
                                                                        size={
                                                                            54
                                                                        }
                                                                        color="inherit"
                                                                        title="progress loading"
                                                                    />
                                                                </Box>
                                                            }
                                                            error={
                                                                <Box className="align-loader">
                                                                    <Typography variant="h3">
                                                                        Document
                                                                        Page
                                                                        Missing
                                                                    </Typography>
                                                                </Box>
                                                            }
                                                            height={height}
                                                            width={width}
                                                            renderTextLayer={
                                                                false
                                                            }
                                                            renderAnnotationLayer={
                                                                false
                                                            }
                                                        />
                                                    ))}
                                                </Document>
                                            )}
                                    </Box>
                                </Box>
                            </FooterWrapper>
                        </FullScreen>
                    </Box>
                </Modal>
            </Box>
        );
    }
);

export default DocumentModal;
